
import React from "react";
import ErrorPage from "@/pages/ErrorPage";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      componentStack: null 
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Add timestamp to error
    const timestamp = new Date().toISOString();
    console.error(`Error caught by ErrorBoundary at ${timestamp}:`, error);
    console.error("Component stack:", errorInfo.componentStack);
    
    // Store component stack for more detailed reporting
    this.setState({
      componentStack: errorInfo.componentStack
    });
    
    // Log more details for specific error types or empty errors
    if (!error.message) {
      console.error("EMPTY ERROR DETECTED - Additional context:");
      console.error("Current URL:", window.location.href);
      console.error("Current route:", window.location.pathname);
      console.error("User agent:", navigator.userAgent);
      
      try {
        // Try to get more context
        const currentComponent = errorInfo.componentStack
          .split('\n')[1]
          .trim()
          .replace(/^at\s+/, '');
        console.error("Error likely originated in component:", currentComponent);
      } catch (e) {
        console.error("Could not determine component from stack");
      }
    }
    
    // Log more details for specific error types
    if (error.message && (
      error.message.includes('does not provide an export') || 
      error.message.includes('Cannot find module') ||
      error.message.includes('Unexpected token') ||
      error.message.includes('is not defined') ||
      error.message.includes('is not a function') ||
      error.message.includes('supabase')
    )) {
      console.error("DETAILED ERROR REPORT:", {
        timestamp,
        errorType: this.categorizeError(error),
        errorMessage: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        url: window.location.href,
        userAgent: navigator.userAgent
      });
    }
  }

  // Helper to categorize errors for better debugging
  categorizeError(error: Error): string {
    if (!error.message) return 'EMPTY_ERROR';
    
    const msg = error.message.toLowerCase();
    
    if (msg.includes('supabase') || msg.includes('database')) {
      return 'DATABASE_CONNECTION';
    } else if (msg.includes('import') || msg.includes('export') || msg.includes('module')) {
      return 'MODULE_LOADING';
    } else if (msg.includes('undefined') || msg.includes('null')) {
      return 'NULL_REFERENCE';
    } else if (msg.includes('function')) {
      return 'FUNCTION_ERROR';
    } else if (msg.includes('promise') || msg.includes('async')) {
      return 'ASYNC_ERROR';
    } else if (msg.includes('network') || msg.includes('fetch') || msg.includes('api')) {
      return 'NETWORK_ERROR';
    } else {
      return 'UNKNOWN_ERROR';
    }
  }

  resetErrorBoundary = () => {
    this.setState({ hasError: false, error: null, componentStack: null });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided, otherwise use ErrorPage
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      // Render the error page with the caught error
      return (
        <ErrorPage 
          error={this.state.error as Error} 
          componentStack={this.state.componentStack}
          resetErrorBoundary={this.resetErrorBoundary} 
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
